import React, { useState } from 'react';
import { useData } from '../../hooks/useData';
import { Avatar } from '@clareefai/ui';
import { Prospect } from '@clareefai/types';
import { ChevronLeft, ChevronRight, Check, Mail, Send, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface RescheduleFormProps {
  prospect: Prospect;
  onClose: () => void;
}

const RescheduleForm: React.FC<RescheduleFormProps> = ({ prospect, onClose }) => {
  const { t } = useTranslation();
  const { promoters } = useData();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPromoters, setSelectedPromoters] = useState<string[]>([]);

  const handlePromoterToggle = (promoterId: string) => {
    setSelectedPromoters(prev => 
      prev.includes(promoterId) 
        ? prev.filter(id => id !== promoterId)
        : [...prev, promoterId]
    );
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Logique de relance
    console.log('Relance du prospect:', prospect.id, 'avec promoteurs:', selectedPromoters);
    onClose();
  };

  const generateEmailTemplate = () => {
    const selectedPromotersList = promoters
      .filter(p => selectedPromoters.includes(p.id))
      .map(p => `${p.name} / ${p.company}`)
      .join('\n\t');

    return [
      t('prospects.reschedule.emailGreeting', { name: prospect.name }),
      '',
      t('prospects.reschedule.emailIntro'),
      '',
      t('prospects.reschedule.emailBody'),
      '',
      `\t${selectedPromotersList}`,
      '',
      t('prospects.reschedule.emailClosing'),
      '',
      t('prospects.reschedule.emailCta'),
      '',
      t('prospects.reschedule.emailSignoff'),
    ].join('\n');
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
            step <= currentStep 
              ? 'bg-blue-600 border-blue-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-16 h-0.5 ${
              step < currentStep ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('prospects.reschedule.selectPromoters')}</h3>
        <p className="text-gray-600">{t('prospects.reschedule.chooseToFollowUp', { name: prospect.name })}</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <RefreshCw className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">{t('prospects.reschedule.prospectFollowUp')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('prospects.reschedule.prospectLabel')} <strong>{prospect.name}</strong> ({prospect.company})
            </p>
            <p className="text-sm text-blue-700">
              {t('prospects.reschedule.currentStatus')} <strong>{
                prospect.status === 'introduce' ? t('prospects.reschedule.statusIntroduce') :
                prospect.status === 'request_sent' ? t('prospects.reschedule.statusRequestSent') :
                prospect.status === 'scheduled' ? t('prospects.reschedule.statusScheduled') :
                prospect.status === 'completed' ? t('prospects.reschedule.statusCompleted') :
                t('prospects.reschedule.statusFailed')
              }</strong>
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {promoters.map((promoter) => (
          <div
            key={promoter.id}
            onClick={() => handlePromoterToggle(promoter.id)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              selectedPromoters.includes(promoter.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-4">
              <Avatar
                src={promoter.avatar}
                name={promoter.name}
                size="md"
              />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{promoter.name}</h4>
                <p className="text-sm text-gray-600">{promoter.company}</p>
                <div className="flex items-center space-x-4 mt-1">
                  <span className="text-xs text-gray-500">{promoter.prospectsReferred} prospects</span>
                  <span className="text-xs text-gray-500">{promoter.conversionRate}% conversion</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    promoter.status === 'available' ? 'bg-green-100 text-green-800' :
                    promoter.status === 'testimonial_done' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {promoter.status === 'available' ? t('prospects.reschedule.statusAvailable') :
                     promoter.status === 'testimonial_done' ? t('prospects.reschedule.statusTestimonialDone') :
                     t('prospects.reschedule.statusOther')}
                  </span>
                </div>
              </div>
              {selectedPromoters.includes(promoter.id) && (
                <Check className="w-5 h-5 text-blue-600" />
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedPromoters.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-900">
              {selectedPromoters.length === 1
                ? t('prospects.reschedule.promoterSelected', { count: selectedPromoters.length })
                : t('prospects.reschedule.promotersSelected', { count: selectedPromoters.length })}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('prospects.reschedule.followUpEmail')}</h3>
        <p className="text-gray-600">{t('prospects.reschedule.emailPreview')}</p>
      </div>

      <div className="bg-gray-50 border rounded-lg p-6">
        <div className="bg-white rounded border p-4">
          <div className="border-b pb-3 mb-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
              <Mail className="w-4 h-4" />
              <span>{t('prospects.reschedule.toLabel', { email: prospect.email })}</span>
            </div>
            <div className="text-sm text-gray-600">
              {t('prospects.reschedule.subjectLabel')}
            </div>
          </div>

          <div className="whitespace-pre-line text-sm text-gray-800 font-mono leading-relaxed">
            {generateEmailTemplate()}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Send className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">{t('prospects.reschedule.emailReady')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('prospects.reschedule.emailReadyDesc', { name: prospect.name })}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <RefreshCw className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900">{t('prospects.reschedule.followUpAction')}</h4>
            <p className="text-sm text-yellow-700 mt-1">
              {t('prospects.reschedule.followUpActionDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <Check className="w-10 h-10 text-green-600" />
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('prospects.reschedule.completedTitle')}</h3>
        <p className="text-gray-600">{t('prospects.reschedule.completedDesc')}</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-3">{t('prospects.reschedule.summary')}</h4>
        <div className="text-left space-y-2 text-sm">
          <div><span className="font-medium">{t('prospects.reschedule.prospect')}</span> {prospect.name}</div>
          <div><span className="font-medium">{t('prospects.reschedule.company')}</span> {prospect.company}</div>
          <div><span className="font-medium">{t('prospects.reschedule.email')}</span> {prospect.email}</div>
          <div><span className="font-medium">{t('prospects.reschedule.promotersContacted')}</span> {selectedPromoters.length}</div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <Send className="w-5 h-5 text-green-600" />
          <span className="font-medium text-green-900">
            {t('prospects.reschedule.emailSentTo', { email: prospect.email })}
          </span>
        </div>
        <p className="text-sm text-green-700 mt-1">
          {t('prospects.reschedule.autoScheduled')}
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {renderStepIndicator()}

      <div className="min-h-96">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          type="button"
          onClick={currentStep === 1 ? onClose : handlePrevious}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          {currentStep === 1 ? (
            t('common.cancel')
          ) : (
            <>
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('common.previous')}
            </>
          )}
        </button>

        {currentStep < 3 ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={currentStep === 1 && selectedPromoters.length === 0}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep === 2 ? t('prospects.reschedule.sendEmailCta') : t('common.next')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            className="btn-primary"
          >
            {t('common.finish')}
          </button>
        )}
      </div>
    </div>
  );
};

export default RescheduleForm;